import { apiFetch } from '../config/api'

export interface HabitAdjustmentCheckin {
  id: string
  habit_def_id: number
  habit_node_id?: number
  habit_title: string
  habit_description?: string | null
  status: string
  detected_window_start?: string
  detected_window_end?: string
  miss_count?: number
  expected_count?: number
  title?: string
  body?: string
}

export interface HabitAdjustmentRecommendationOption {
  action_id: string
  label: string
}

export interface HabitAdjustmentDecision {
  id: string
  status?: string
  selected_reasons?: string[]
  suggested_strategy?: string
  chosen_action_id?: string | null
}

export interface HabitAdjustmentRecommendation {
  strategy: string
  copy_key: string
  title: string
  body: string
  options: HabitAdjustmentRecommendationOption[]
}

export interface HabitAdjustmentDecisionResponse {
  success: boolean
  decision?: HabitAdjustmentDecision
  recommendation?: HabitAdjustmentRecommendation
}

export interface HabitAdjustmentRespondResponse {
  success: boolean
  checkin?: { id: string; status?: string }
  next_step: 'diagnose' | 'choose_revisit_date' | null
}

export interface HabitAdjustmentChooseActionResponse {
  success: boolean
  decision: HabitAdjustmentDecision
  confirmation: {
    action_id: string
    patch: Record<string, unknown>
  }
}

export interface HabitAdjustmentConfirmResponse {
  success: boolean
  decision_id: string
  patch: Record<string, unknown>
}

export interface HabitAdjustmentRevisitDueItem {
  habit_def_id: number
  habit_title: string
  paused_until?: string
  pause_reason_codes?: string[]
  message?: string
  actions?: string[]
}

export type HabitAdjustmentSimpleResponse = { success: boolean }

async function parseJson<T>(responseOrPromise: Response | Promise<Response>): Promise<T> {
  const response = await responseOrPromise
  if (!response.ok) {
    const text = await response.text().catch(() => '')
    throw new Error(text || `Request failed: ${response.status}`)
  }
  return response.json() as Promise<T>
}

export const habitAdjustmentService = {
  getCheckin(surface: 'today' | 'habit_detail', habitDefId?: number) {
    const params = new URLSearchParams({ surface })
    if (habitDefId) params.set('habit_def_id', String(habitDefId))
    return parseJson<{ success: boolean; checkin: HabitAdjustmentCheckin | null; checkins?: HabitAdjustmentCheckin[] }>(
      apiFetch(`/api/automom/v1/habits/checkins?${params.toString()}`)
    )
  },

  getRevisitDue(surface: 'today' = 'today') {
    return parseJson<{ success: boolean; revisit_due: HabitAdjustmentRevisitDueItem[] }>(
      apiFetch(`/api/automom/v1/habits/revisit-due?surface=${surface}`)
    )
  },

  markShown(checkinId: string) {
    return parseJson<{ success: boolean; first_shown: boolean }>(
      apiFetch(`/api/automom/v1/habits/checkins/${checkinId}/shown`, { method: 'POST' })
    )
  },

  respond(checkinId: string, response: 'adjust' | 'pause' | 'keep_as_is' | 'not_now') {
    return parseJson<HabitAdjustmentRespondResponse>(
      apiFetch(`/api/automom/v1/habits/checkins/${checkinId}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ response }),
      })
    )
  },

  diagnose(checkinId: string, reasons: string[]) {
    return parseJson<HabitAdjustmentDecisionResponse>(
      apiFetch(`/api/automom/v1/habits/checkins/${checkinId}/diagnose`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reasons }),
      })
    )
  },

  chooseAction(decisionId: string, actionId: string, details?: Record<string, unknown>) {
    return parseJson<HabitAdjustmentChooseActionResponse>(
      apiFetch(`/api/automom/v1/habits/decisions/${decisionId}/choose-action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action_id: actionId, details }),
      })
    )
  },

  confirm(decisionId: string) {
    return parseJson<HabitAdjustmentConfirmResponse>(
      apiFetch(`/api/automom/v1/habits/decisions/${decisionId}/confirm`, {
        method: 'POST',
      })
    )
  },

  pause(habitDefId: number, payload: { paused_until?: string; reason_codes?: string[]; source?: string }) {
    return parseJson<HabitAdjustmentSimpleResponse>(
      apiFetch(`/api/automom/v1/habits/${habitDefId}/pause`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
    )
  },

  restart(habitDefId: number) {
    return parseJson<HabitAdjustmentSimpleResponse>(
      apiFetch(`/api/automom/v1/habits/${habitDefId}/restart`, { method: 'POST' })
    )
  },

  archive(habitDefId: number, payload: { source?: string; reason?: string } = {}) {
    return parseJson<HabitAdjustmentSimpleResponse>(
      apiFetch(`/api/automom/v1/habits/${habitDefId}/archive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
    )
  },
}
